/**
 * Color Definitions for Showpass Theme
 *
 * This file defines the raw color values with light/dark variants.
 * The actual theme is configured in `packages/next-app/theme.ts`.
 *
 * ## Structure
 *
 * - `colorModes` - All color palettes with light/dark variants
 * - `brandSemanticTokens` - Semantic tokens for brand/status colors
 *
 * ## Usage
 *
 * Import `colorModes` to access raw values:
 * ```ts
 * import { colorModes } from "@shared/theme/props/colors";
 * colorModes.primary[500].light // "#14595F"
 * ```
 *
 * Import `brandColors` for immutable brand colors (logos, etc.):
 * ```ts
 * import { brandColors } from "@shared/theme/props/colors";
 * brandColors.primary[500].light // "#14595F" - never changes
 * ```
 *
 * Import `brandSemanticTokens` for theme semantic tokens:
 * ```ts
 * import { brandSemanticTokens } from "@shared/theme/props/colors";
 * // Spread into theme.ts semanticTokens
 * ```
 */

// =============================================================================
// BRAND COLORS - Immutable Showpass brand colors (never customized)
// Use these for logos and brand elements that should not change with themes
// =============================================================================

const brandColors = {
  // === BRAND TEAL (Primary) ===
  // Light default: 600 (#24757C) | Dark default: 600 (#24757C)
  primary: {
    100: { light: "#CDE8EB", dark: "#072D30" },
    200: { light: "#BDDBDE", dark: "#0C3F43" },
    300: { light: "#A7CCCF", dark: "#14595F" },
    400: { light: "#85B6BA", dark: "#1A6970" },
    500: { light: "#328C94", dark: "#1F6F77" },
    600: { light: "#24757C", dark: "#24757C" },
    700: { light: "#1A6970", dark: "#328C94" },
    800: { light: "#14595F", dark: "#85B6BA" },
    900: { light: "#0C3F43", dark: "#A7CCCF" },
    950: { light: "#072D30", dark: "#BDDBDE" },
  },

  // === BRAND RED (Secondary) ===
  // Light default: 600 (#DD3333) | Dark default: 600 (#DD3333)
  secondary: {
    100: { light: "#F9E6E6", dark: "#650404" },
    200: { light: "#EAC5C5", dark: "#8C1212" },
    300: { light: "#E8AAAA", dark: "#BC1B1B" },
    400: { light: "#E57777", dark: "#CA1E1E" },
    500: { light: "#DF4F4F", dark: "#D42929" },
    600: { light: "#DD3333", dark: "#DD3333" },
    700: { light: "#CA1E1E", dark: "#DF4F4F" },
    800: { light: "#BC1B1B", dark: "#E57777" },
    900: { light: "#8C1212", dark: "#E8AAAA" },
    950: { light: "#650404", dark: "#EAC5C5" },
  },
};

// =============================================================================
// COLOR MODES - Raw color values with light/dark variants
// Primary and secondary reference brandColors (can be overridden in custom themes)
// =============================================================================

export const colorModes = {
  // === BASE COLORS ===
  font: {
    light: "#11142D",
    dark: "#D9D9D9",
  },
  charcoal: {
    light: "#292929",
    dark: "#C5C5C5",
  },
  midnight: {
    light: "#000000",
    dark: "#E5E5E5",
  },
  white: {
    light: "#FFFFFF",
    dark: "#202020",
  },
  offWhite: {
    light: "#F7F7F7",
    dark: "#191919",
  },

  brandPrimary: brandColors.primary,
  brandSecondary: brandColors.secondary,

  // === SOCIAL BRAND COLORS ===
  // Third-party brand colors (static, don't invert in dark mode)
  facebookBlue: {
    light: "#1877F2",
    dark: "#1877F2",
  },

  // === GRAY (Neutral - no blue tint) ===
  // Light default: 600 (#737373) | Dark default: 600 (#737373)
  gray: {
    100: { light: "#FAFAFA", dark: "#171717" },
    200: { light: "#F5F5F5", dark: "#262626" },
    300: { light: "#E5E5E5", dark: "#404040" },
    400: { light: "#D4D4D4", dark: "#525252" },
    500: { light: "#A3A3A3", dark: "#636363" },
    600: { light: "#737373", dark: "#737373" },
    700: { light: "#525252", dark: "#A3A3A3" },
    800: { light: "#404040", dark: "#D4D4D4" },
    900: { light: "#262626", dark: "#E5E5E5" },
    950: { light: "#171717", dark: "#F5F5F5" },
  },

  // === RED/ERROR ===
  // Light default: 600 (#EF4444) | Dark default: 600 (#EF4444)
  red: {
    100: { light: "#FEF2F2", dark: "#7F1D1D" },
    200: { light: "#FEE2E2", dark: "#991B1B" },
    300: { light: "#FECACA", dark: "#B91C1C" },
    400: { light: "#FCA5A5", dark: "#DC2626" },
    500: { light: "#F87171", dark: "#E23939" },
    600: { light: "#EF4444", dark: "#EF4444" },
    700: { light: "#DC2626", dark: "#F87171" },
    800: { light: "#B91C1C", dark: "#FCA5A5" },
    900: { light: "#991B1B", dark: "#FECACA" },
    950: { light: "#7F1D1D", dark: "#FEE2E2" },
  },

  // === GREEN/SUCCESS ===
  // Light default: 600 (#22C55E) | Dark default: 600 (#22C55E)
  green: {
    100: { light: "#F0FDF4", dark: "#14532D" },
    200: { light: "#DCFCE7", dark: "#166534" },
    300: { light: "#BBF7D0", dark: "#15803D" },
    400: { light: "#86EFAC", dark: "#16A34A" },
    500: { light: "#4ADE80", dark: "#1CB454" },
    600: { light: "#22C55E", dark: "#22C55E" },
    700: { light: "#16A34A", dark: "#4ADE80" },
    800: { light: "#15803D", dark: "#86EFAC" },
    900: { light: "#166534", dark: "#BBF7D0" },
    950: { light: "#14532D", dark: "#DCFCE7" },
  },

  // === YELLOW/WARNING ===
  // Light default: 600 (#EAB308) | Dark default: 600 (#EAB308)
  yellow: {
    100: { light: "#FEFCE8", dark: "#713F12" },
    200: { light: "#FEF9C3", dark: "#854D0E" },
    300: { light: "#FEF08A", dark: "#A16207" },
    400: { light: "#FDE047", dark: "#CA8A04" },
    500: { light: "#FACC15", dark: "#DC9A06" },
    600: { light: "#EAB308", dark: "#EAB308" },
    700: { light: "#CA8A04", dark: "#FACC15" },
    800: { light: "#A16207", dark: "#FDE047" },
    900: { light: "#854D0E", dark: "#FEF08A" },
    950: { light: "#713F12", dark: "#FEF9C3" },
  },

  // === BLUE/INFO ===
  // Light default: 600 (#3B82F6) | Dark default: 600 (#3B82F6)
  blue: {
    100: { light: "#EFF6FF", dark: "#1E3A8A" },
    200: { light: "#DBEAFE", dark: "#1E40AF" },
    300: { light: "#BFDBFE", dark: "#1D4ED8" },
    400: { light: "#93C5FD", dark: "#2563EB" },
    500: { light: "#60A5FA", dark: "#2F72F0" },
    600: { light: "#3B82F6", dark: "#3B82F6" },
    700: { light: "#2563EB", dark: "#60A5FA" },
    800: { light: "#1D4ED8", dark: "#93C5FD" },
    900: { light: "#1E40AF", dark: "#BFDBFE" },
    950: { light: "#1E3A8A", dark: "#DBEAFE" },
  },

  // === INDIGO ===
  // Light default: 600 (#6366F1) | Dark default: 600 (#6366F1)
  indigo: {
    100: { light: "#EEF2FF", dark: "#312E81" },
    200: { light: "#E0E7FF", dark: "#3730A3" },
    300: { light: "#C7D2FE", dark: "#4338CA" },
    400: { light: "#A5B4FC", dark: "#4F46E5" },
    500: { light: "#818CF8", dark: "#5A56EB" },
    600: { light: "#6366F1", dark: "#6366F1" },
    700: { light: "#4F46E5", dark: "#818CF8" },
    800: { light: "#4338CA", dark: "#A5B4FC" },
    900: { light: "#3730A3", dark: "#C7D2FE" },
    950: { light: "#312E81", dark: "#E0E7FF" },
  },

  // === PURPLE ===
  // Light default: 600 (#8B5CF6) | Dark default: 600 (#8B5CF6)
  purple: {
    100: { light: "#FAF5FF", dark: "#4C1D95" },
    200: { light: "#F3E8FF", dark: "#4B0FAB" },
    300: { light: "#E9D5FF", dark: "#5B19C2" },
    400: { light: "#D8B4FE", dark: "#7C3AED" },
    500: { light: "#C084FC", dark: "#844BF1" },
    600: { light: "#8B5CF6", dark: "#8B5CF6" },
    700: { light: "#7C3AED", dark: "#C084FC" },
    800: { light: "#5B19C2", dark: "#D8B4FE" },
    900: { light: "#4B0FAB", dark: "#E9D5FF" },
    950: { light: "#4C1D95", dark: "#F3E8FF" },
  },

  // === PINK ===
  // Light default: 600 (#EC4899) | Dark default: 600 (#EC4899)
  pink: {
    100: { light: "#FDF2F8", dark: "#831843" },
    200: { light: "#FCE7F3", dark: "#9D174D" },
    300: { light: "#FBCFE8", dark: "#BE185D" },
    400: { light: "#F9A8D4", dark: "#DB2777" },
    500: { light: "#F472B6", dark: "#E43388" },
    600: { light: "#EC4899", dark: "#EC4899" },
    700: { light: "#DB2777", dark: "#F472B6" },
    800: { light: "#BE185D", dark: "#F9A8D4" },
    900: { light: "#9D174D", dark: "#FBCFE8" },
    950: { light: "#831843", dark: "#FCE7F3" },
  },

  // === EFFECT COLORS ===
  shimmer: {
    highlight: {
      light: "rgba(255, 255, 255, 0.8)",
      dark: "rgba(32, 32, 32, 0.8)",
    },
  },

  // === PRIMARY & SECONDARY ===
  // Most of the time these should be referenced, unless you are styling
  // Showpass-branded elements that should not be affected by custom themes.
  // Use brandPrimary/brandSecondary for immutable brand colors (logos, etc.)
  primary: brandColors.primary,
  secondary: brandColors.secondary,
};

// =============================================================================
// BRAND SEMANTIC TOKENS - Shared brand/status color tokens
// Import and spread into theme.ts semanticTokens.colors
// =============================================================================

export type SemanticTokenValue = { value: { _light: string; _dark: string } };

type PaletteShade = { light: string; dark: string } | string;
type PaletteInput = Record<string, PaletteShade>;

/** Helper to extract light/dark values from either palette format */
export const getColor = (
  shade: PaletteShade,
  mode: "light" | "dark"
): string => {
  if (typeof shade === "string") return shade;
  return shade[mode];
};

/**
 * Generate a full color palette with NESTED structure for Chakra's colorPalette.
 * Returns both numbered shades (100-950) AND semantic tokens (solid, fg, muted, etc.)
 *
 * Used by both default theme and custom theme generation (generateTheme).
 *
 * @param palette - Color palette with shades (100-950)
 * @param type - "primary" or "secondary" (affects fg token dark mode value)
 */
export function generateFullPalette(
  palette: PaletteInput
): Record<string, SemanticTokenValue> {
  return {
    // Numbered shades (100-950)
    ...Object.fromEntries(
      Object.entries(palette).map(([shade, color]) => [
        shade,
        {
          value: {
            _light: getColor(color, "light"),
            _dark: getColor(color, "dark"),
          },
        },
      ])
    ),
    // Semantic tokens (nested inside palette for colorPalette support)
    // Button states: solid=600, hover=800, pressed=400, disabled=200
    solid: {
      value: {
        _light: getColor(palette[600], "light"),
        _dark: getColor(palette[600], "dark"),
      },
    },
    contrast: { value: { _light: "#FFFFFF", _dark: "#FFFFFF" } },
    fg: {
      value: {
        _light: getColor(palette[800], "light"),
        _dark: getColor(palette[700], "dark"), // Uses inverted light shade for readability
      },
    },
    bg: {
      value: {
        _light: getColor(palette[800], "light"),
        _dark: getColor(palette[200], "dark"),
      },
    },
    muted: {
      value: {
        _light: getColor(palette[100], "light"),
        _dark: getColor(palette[900], "dark"),
      },
    },
    subtle: {
      value: {
        _light: getColor(palette[300], "light"),
        _dark: getColor(palette[950], "dark"),
      },
    },
    // Hover state (800)
    emphasized: {
      value: {
        _light: getColor(palette[800], "light"),
        _dark: getColor(palette[400], "dark"),
      },
    },
    // Pressed/Active state (400)
    pressed: {
      value: {
        _light: getColor(palette[400], "light"),
        _dark: getColor(palette[800], "dark"),
      },
    },
    // Disabled state (200)
    disabled: {
      value: {
        _light: getColor(palette[200], "light"),
        _dark: getColor(palette[900], "dark"),
      },
    },
    focusRing: {
      value: {
        _light: getColor(palette[600], "light"),
        _dark: getColor(palette[600], "dark"),
      },
    },

    // === DARK VARIANTS (for dark buttons) ===
    // Light mode: dark bg (800) with white text
    // Dark mode: light bg (400) with dark text
    // Button states: solidDark=800, hover=900, pressed=700, disabled=200
    solidDark: {
      value: {
        _light: getColor(palette[800], "light"),
        _dark: getColor(palette[800], "dark"),
      },
    },
    // Hover state for dark buttons (900)
    emphasizedDark: {
      value: {
        _light: getColor(palette[900], "light"),
        _dark: getColor(palette[900], "dark"),
      },
    },
    // Pressed/Active state for dark buttons (700)
    pressedDark: {
      value: {
        _light: getColor(palette[700], "light"),
        _dark: getColor(palette[500], "dark"),
      },
    },
    // Disabled state for dark buttons (200)
    disabledDark: {
      value: {
        _light: getColor(palette[500], "light"),
        _dark: getColor(palette[500], "dark"),
      },
    },
    // Contrast color for dark buttons (dark text in dark mode)
    contrastDark: {
      value: {
        _light: "#FFFFFF", // White text on dark bg in light mode
        _dark: getColor(palette[200], "dark"), // Dark text on light bg in dark mode
      },
    },
  };
}

/**
 * Generate the bg.subtle token as a flat key (Chakra nested token syntax).
 * Must be spread separately since "bg.subtle" can't be nested inside "bg".
 */
export function generateBgSubtleToken(
  palette: PaletteInput,
  prefix: string
): Record<string, SemanticTokenValue> {
  return {
    [`${prefix}.bg.subtle`]: {
      value: {
        _light: getColor(palette[800], "light"),
        _dark: getColor(palette[100], "dark"),
      },
    },
  };
}

/**
 * Generate status color palette (success, error, warning, info).
 * Similar to generateFullPalette but without numbered shades.
 * Returns NESTED structure for colorPalette support.
 *
 * @param palette - Color palette with shades (100-950)
 * @param solidShade - Which shade to use for .solid (default 600, warning uses 500)
 */
export function generateStatusPalette(
  palette: PaletteInput,
  solidShade = 600
): Record<string, SemanticTokenValue> {
  return {
    // Button states: solid=600, hover=800, pressed=400, disabled=200
    solid: {
      value: {
        _light: getColor(palette[solidShade], "light"),
        _dark: getColor(palette[solidShade], "dark"),
      },
    },
    contrast: { value: { _light: "#FFFFFF", _dark: "#FFFFFF" } },
    fg: {
      value: {
        _light: getColor(palette[800], "light"),
        _dark: getColor(palette[200], "dark"),
      },
    },
    bg: {
      value: {
        _light: getColor(palette[600], "light"),
        _dark: getColor(palette[200], "dark"),
      },
    },
    muted: {
      value: {
        _light: getColor(palette[100], "light"),
        _dark: getColor(palette[900], "dark"),
      },
    },
    subtle: {
      value: {
        _light: getColor(palette[200], "light"),
        _dark: getColor(palette[950], "dark"),
      },
    },
    // Hover state (800)
    emphasized: {
      value: {
        _light: getColor(palette[800], "light"),
        _dark: getColor(palette[400], "dark"),
      },
    },
    // Pressed/Active state (400)
    pressed: {
      value: {
        _light: getColor(palette[400], "light"),
        _dark: getColor(palette[800], "dark"),
      },
    },
    // Disabled state (200)
    disabled: {
      value: {
        _light: getColor(palette[200], "light"),
        _dark: getColor(palette[900], "dark"),
      },
    },
    focusRing: {
      value: {
        _light: getColor(palette[600], "light"),
        _dark: getColor(palette[600], "dark"), // Uses 600 pivot point
      },
    },

    // === DARK VARIANTS (for dark buttons) ===
    // Light mode: dark bg (800) with white text
    // Dark mode: light bg (400) with dark text
    // Button states: solidDark=800, hover=900, pressed=700, disabled=200
    solidDark: {
      value: {
        _light: getColor(palette[800], "light"),
        _dark: getColor(palette[800], "dark"),
      },
    },
    // Hover state for dark buttons (900)
    emphasizedDark: {
      value: {
        _light: getColor(palette[900], "light"),
        _dark: getColor(palette[900], "dark"),
      },
    },
    // Pressed/Active state for dark buttons (700)
    pressedDark: {
      value: {
        _light: getColor(palette[700], "light"),
        _dark: getColor(palette[700], "dark"),
      },
    },
    // Disabled state for dark buttons (200)
    disabledDark: {
      value: {
        _light: getColor(palette[200], "light"),
        _dark: getColor(palette[900], "dark"),
      },
    },
    // Contrast color for dark buttons (dark text in dark mode)
    contrastDark: {
      value: {
        _light: "#FFFFFF", // White text on dark bg in light mode
        _dark: getColor(palette[200], "dark"), // Dark text on light bg in dark mode
      },
    },
  };
}

/**
 * Status color semantic tokens (success, error, warning, info).
 * Uses NESTED structure for colorPalette support.
 *
 * NOTE: Primary and secondary brand tokens are generated using
 * generateFullPalette() in theme.ts for proper colorPalette support.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const brandSemanticTokens: Record<string, any> = {
  // === STATUS COLORS (nested for colorPalette support) ===
  success: generateStatusPalette(colorModes.green),
  error: generateStatusPalette(colorModes.red),
  warning: generateStatusPalette(colorModes.yellow),
  info: generateStatusPalette(colorModes.blue),
};
